// Per-install branding + terminology overrides.
//
// Branding is global (not tenant-scoped) so the public `/api/v1/branding`
// read works pre-auth — the sign-in chrome can paint with the operator's
// logo + color before any session exists. Master writes via
// `/api/v1/admin/branding` (gated on MASTER_MANAGE_BRANDING).
//
// `term(key, fallback)` is the operator-facing terminology lookup —
// pages read e.g. `term('resident', 'Residents')` and the user sees
// whatever label the master set (or the fallback if unset).

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiClient } from './api.js';
import { DEFAULT_BRANDING, type Branding } from '@ng-unifi/domain';

interface BrandingContextValue {
  branding: Branding;
  refresh: () => Promise<void>;
  term: (key: 'resident' | 'employee' | 'building', fallback: string) => string;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);

  const refresh = async () => {
    try {
      const res = await apiClient.get<{ data: Branding }>('/api/v1/branding', {
        skipTenantScope: true,
      });
      setBranding(res.data);
    } catch {
      // Tolerate fresh-install / pre-setup 404 — fall back to defaults.
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo<BrandingContextValue>(
    () => ({
      branding,
      refresh,
      term: (key, fallback) => {
        const t = branding.terminology;
        if (key === 'resident') return t.resident_label ?? fallback;
        if (key === 'employee') return t.employee_label ?? fallback;
        return t.building_label ?? fallback;
      },
    }),
    [branding],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error(
      'useBranding must be used inside a <BrandingProvider>. ' +
        'Wrap your test render with <BrandingProvider>.',
    );
  }
  return ctx;
}
